import { useQuery } from '@apollo/client'
import {
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../../../../data/gql/questionnaire/queries'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import {
  MappingCell,
  MappingRow,
  computeMarketMapping
} from '../../../../modules/Questionnaire/MarketVisibility/computeMarketMapping'
import {
  shortMarketLabel,
  useProjectMarkets
} from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { flattenEntries } from '../../../../modules/Questionnaire/Questionnaire.utils'
import { ProjectFlowBreadcrumb } from '../ProjectFlowBreadcrumb'
import { getMasterSurvey, isProjectInProgress } from '../markets.utils'

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  editLink: {
    color: 'var(--primary)',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 14
  },
  panel: {
    border: '1px solid #E5EAF5',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#FFFFFF'
  },
  headRow: {
    display: 'grid',
    padding: '14px 16px',
    borderBottom: '1px solid #E5EAF5',
    color: '#6B7280',
    alignItems: 'center'
  },
  // A market's audience label can be long, so the heading wraps within its
  // column rather than widening the grid.
  marketHead: { textAlign: 'center', overflowWrap: 'anywhere' },
  // Cap the rows area so a long questionnaire scrolls within the panel instead
  // of growing the page; the column header above stays fixed.
  body: { maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' },
  row: {
    display: 'grid',
    borderBottom: '1px solid #F1F3F7',
    alignItems: 'stretch'
  },
  questionCell: {
    display: 'flex',
    gap: 8,
    alignItems: 'baseline',
    padding: '14px 16px',
    minWidth: 0
  },
  identifier: { fontWeight: 700, color: '#42526E', whiteSpace: 'nowrap' },
  questionText: {
    color: '#172B4D',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  rowEditLink: {
    color: 'var(--primary)',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap'
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 8px',
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center'
  },
  cellCommon: { background: '#EAF7EE', color: '#1B7F4B' },
  cellOpts: { background: '#FDF3DC', color: '#A86B00' },
  cellExcluded: { background: '#F4F5F7', color: '#8993A4' },
  empty: { color: '#6B7280', padding: '24px 16px' }
})

const cellLabel = (row: MappingRow, cell: MappingCell): string => {
  if (cell.excluded) return '✗ Excluded'
  // Common = present + full in every market; Included = full in this market but
  // the question isn't uniform across all markets. Only a reduced set counts.
  if (row.isCommon) return '✓ Common'
  if (cell.full) return '✓ Included'
  return `${cell.visible} opts`
}

const cellVariantClass = (
  cell: MappingCell,
  classes: Record<'cellCommon' | 'cellOpts' | 'cellExcluded', string>
): string => {
  if (cell.excluded) return classes.cellExcluded
  if (cell.full) return classes.cellCommon
  return classes.cellOpts
}

const MappingPageContent = () => {
  const { classes, cx } = useStyles()
  const projectId = useProjectId()
  const waveId = useOptionalWaveId()
  const { project } = useGetProjectByProjectId()
  const markets = useProjectMarkets()
  // The shared questionnaire is editable only while the project is still in
  // Draft; once any market launches it is read-only, so the edit links hide.
  const projectInProgress = isProjectInProgress(project)

  // Read the full questionnaire keyed by the base/master survey (same variables
  // as the editor) so the shared normalized cache entry stays consistent; the
  // mapping grid applies per-market visibility itself.
  const { data, loading } = useQuery<QuestionnaireData>(QUESTIONNAIRE, {
    context: { clientName: 'questionnaire' },
    variables: {
      projectId,
      surveyId: getMasterSurvey(project)?.surveyId,
      waveId
    }
  })

  const rows = useMemo(
    () =>
      computeMarketMapping(
        flattenEntries(data?.draftQuestionnaire.entries ?? []),
        markets.map((market) => market.surveyId)
      ),
    [data?.draftQuestionnaire.entries, markets]
  )

  const surveyHref = `/project/${projectId}/survey`
  const gridTemplateColumns = `minmax(0, 2fr) ${markets
    .map(() => 'minmax(0, 1fr)')
    .join(' ')}`

  return (
    <div>
      <ProjectFlowBreadcrumb activeStep="Mapping" />

      <div className={classes.page}>
        <div className={classes.header}>
          <Text size={TextSize.xl} weight={TextWeight.Bold}>
            Question × Market Mapping
          </Text>
          {!projectInProgress && (
            <Link to={surveyHref} className={classes.editLink}>
              Edit in Survey →
            </Link>
          )}
        </div>

        {markets.length === 0 ? (
          <div className={classes.panel}>
            <div className={classes.empty}>
              <Text size={TextSize.ms}>
                No markets yet. Add a market on the Audiences step to see its
                mapping here.
              </Text>
            </div>
          </div>
        ) : (
          <div className={classes.panel}>
            <div className={classes.headRow} style={{ gridTemplateColumns }}>
              <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                QUESTION
              </Text>
              {markets.map((market) => (
                <div key={market.surveyId} className={classes.marketHead}>
                  <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                    {market.flag ? `${market.flag} ` : ''}
                    {shortMarketLabel(market)}
                  </Text>
                </div>
              ))}
            </div>

            <div className={classes.body}>
              {loading ? (
                <div className={classes.empty}>
                  <Text size={TextSize.ms}>Loading…</Text>
                </div>
              ) : rows.length === 0 ? (
                <div className={classes.empty}>
                  <Text size={TextSize.ms}>No questions yet.</Text>
                </div>
              ) : (
                rows.map((row) => (
                  <div
                    key={row.id}
                    className={classes.row}
                    style={{ gridTemplateColumns }}
                  >
                    <div className={classes.questionCell}>
                      <span className={classes.identifier}>
                        <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                          {row.identifier}
                        </Text>
                      </span>
                      <span className={classes.questionText}>
                        <Text size={TextSize.ms}>{row.text}</Text>
                      </span>
                      {!projectInProgress && (
                        <Link to={surveyHref} className={classes.rowEditLink}>
                          Edit →
                        </Link>
                      )}
                    </div>
                    {row.cells.map((cell, index) => (
                      <div
                        key={markets[index].surveyId}
                        className={cx(
                          classes.cell,
                          cellVariantClass(cell, classes)
                        )}
                      >
                        {cellLabel(row, cell)}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const MappingPage = () => <MappingPageContent />
